import type { Project } from '../components/minimal';
import {
  faLinux,
  faApple,
  faChrome,
  faSafari,
  faGithub,
  faNpm,
  faRust,
  faPython,
} from '@fortawesome/free-brands-svg-icons';
import { faBrain } from '@fortawesome/free-solid-svg-icons';
import { vulnerabilities } from './research';
import { openSource, apps } from './portfolio';

const withIcon = (p: Project, extra: Partial<Project>): Project => ({ ...p, ...extra });

const byTitle = (title: string) => {
  const found = vulnerabilities.find(v => v.title === title);
  if (!found) throw new Error(`Vulnerability not found: ${title}`);
  return found;
};

export const kernel: Project[] = [
  withIcon(byTitle('Linux Kernel io_uring OOB Read'), {
    icon: faLinux,
    iconColor: '#fbbf24',
    iconBg: 'rgba(251, 191, 36, 0.1)',
  }),
  withIcon(byTitle('XNU Kernel NECP Client UAF'), {
    icon: faApple,
    iconColor: '#e5e5e5',
    iconBg: 'rgba(229, 229, 229, 0.08)',
  }),
];

export const browsers: Project[] = [
  withIcon(byTitle('V8 Maglev JIT Uninitialized Memory'), {
    icon: faChrome,
    iconColor: '#4285f4',
    iconBg: 'rgba(66, 133, 244, 0.1)',
  }),
  withIcon(byTitle('WebKit / Safari — 5 Bugs'), {
    icon: faSafari,
    iconColor: '#1b88ca',
    iconBg: 'rgba(27, 136, 202, 0.1)',
  }),
];

export const tools: Project[] = openSource.map(p => {
  if (p.title === 'Flowdown') {
    return { ...p, icon: faNpm, iconColor: '#cb3837', iconBg: 'rgba(203, 56, 55, 0.1)' };
  }
  if (p.title === 'Logpack') {
    return { ...p, icon: faRust, iconColor: '#dea584', iconBg: 'rgba(222, 165, 132, 0.1)' };
  }
  if (p.title === 'Veil API') {
    return { ...p, icon: faPython, iconColor: '#3776ab', iconBg: 'rgba(55, 118, 171, 0.1)' };
  }
  return p;
});

export const buildApps: Project[] = apps.map(p => ({
  ...p,
  icon: faApple,
  iconColor: '#e5e5e5',
  iconBg: 'rgba(229, 229, 229, 0.08)',
}));

export const writeups: Project[] = [
  {
    title: 'Linux io_uring SQE_MIXED OOB Read — Writeup & PoC',
    description:
      'Full root-cause analysis of the SQE_MIXED sq_array boundary check bug, with reliable PoC. Upstream patch merged by Jens Axboe, backported to 6.19-stable.',
    status: 'published',
    statusLabel: 'GIST',
    tags: ['kernel', 'io_uring', 'writeup'],
    icon: faGithub,
    iconColor: '#e5e5e5',
    iconBg: 'rgba(229, 229, 229, 0.08)',
    links: [
      {
        label: 'Read',
        url: 'https://gist.github.com/Atomics-hub/88b2c497166c838cc63633c5ce32c259',
      },
    ],
  },
  {
    title: 'V8 Maglev SaveCallSpeculationScope Uninitialized Memory',
    description:
      "Writeup of the P1/S1 Chromium bug in V8's Maglev JIT speculation scope handling. Fixed same day by the V8 team.",
    status: 'published',
    statusLabel: 'GIST',
    tags: ['V8', 'JIT', 'writeup'],
    icon: faGithub,
    iconColor: '#e5e5e5',
    iconBg: 'rgba(229, 229, 229, 0.08)',
    links: [
      { label: 'Read', url: 'https://gist.github.com/Atomics-hub/baf37d7f7402a5011bbed61208872339' },
    ],
  },
  {
    title:
      'What Must Be Preserved? Mapping Theories of Consciousness to Engineering Requirements for Mind Preservation',
    description:
      'Published paper evaluating 8 consciousness theories against engineering requirements for mind preservation. Zenodo, DOI 10.5281/zenodo.19374628.',
    status: 'published',
    statusLabel: 'PAPER',
    tags: ['consciousness', 'philosophy of mind', 'Zenodo'],
    icon: faBrain,
    iconColor: '#a78bfa',
    iconBg: 'rgba(167, 139, 250, 0.1)',
    links: [
      { label: 'Read Paper', url: 'https://doi.org/10.5281/zenodo.19374628' },
      { label: 'GitHub', url: 'https://github.com/Atomics-hub/consciousness-research' },
    ],
  },
];

export const buildStats = {
  bugsFixed: '4',
  productsLive: '4',
  monthlyReach: '650k+',
};

export const buildBio =
  'Security researcher and engineer. I find bugs in kernels and browsers, ship developer tools, and write about consciousness.';
